import React, { useMemo } from "react";
import { Box, Typography, Card, CardContent, useTheme, useMediaQuery } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { HistoricalChart30Min } from "../../../../types/StockAnalysisReport";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatStockPrice, formatStockPriceCompact } from "../../../../utils/stockPriceFormatter";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ReportPriceChartProps {
  priceData: HistoricalChart30Min[];
  ticker: string;
  height?: number;
}

interface ChartDataPoint {
  x: number;
  y: number;
  originalDate: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const ReportPriceChart: React.FC<ReportPriceChartProps> = ({
  priceData,
  ticker,
  height = 300,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currency, currencySymbol } = useStockCurrency();

  // Helper function for currency formatting - uses stock currency context
  const formatCurrency = (value: number): string => {
    return formatStockPrice({ price: value, currency });
  };

  const {
    chartData,
    priceChange,
    priceChangePercent,
    sortedData,
    formatAxisDate,
    minPrice,
  } = useMemo(() => {
    if (!priceData || priceData.length === 0) {
      return {
        chartData: [],
        priceChange: 0,
        priceChangePercent: 0,
        sortedData: [],
        formatAxisDate: () => "",
        minPrice: 0,
      };
    }

    // Sort data by date (oldest first for chart)
    const sortedData = [...priceData].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Create evenly spaced points, skipping non-trading days
    const points: ChartDataPoint[] = sortedData.map((item, index) => ({
      x: index, // Use sequential index for even spacing
      y: item.close,
      originalDate: item.date,
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
      volume: item.volume,
    }));

    // Calculate price change over the period
    const firstPrice = sortedData[0]?.close || 0;
    const lastPrice = sortedData[sortedData.length - 1]?.close || 0;
    const change = lastPrice - firstPrice;
    const changePercent = firstPrice !== 0 ? (change / firstPrice) * 100 : 0;

    // Calculate minimum price for area baseline - round down to 2 decimal places
    const dataMinPrice = Math.min(...sortedData.map((item) => item.low));
    const minPrice = Math.floor(dataMinPrice * 100) / 100;

    // Create format function for axis labels
    const formatAxisDate = (value: any) => {
      const dataIndex = Math.floor(value as number);
      if (dataIndex >= 0 && dataIndex < sortedData.length) {
        return dayjs(sortedData[dataIndex].date).format("MMM DD");
      }
      return "";
    };

    return {
      chartData: [
        {
          id: ticker,
          data: points,
        },
      ],
      priceChange: change,
      priceChangePercent: changePercent,
      sortedData,
      formatAxisDate,
      minPrice,
    };
  }, [priceData, ticker]);

  const lineColor = priceChange >= 0 ? "#22c55e" : "#ef4444";
  const isPositive = priceChange >= 0;

  if (!priceData || priceData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <Card
          sx={{
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            width: "70%",
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              }}
            >
              Price data not available for this report period
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
      <Card
        sx={{
          borderRadius: isMobile ? 0 : 3,
          backgroundColor:
            theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
          border: isMobile
            ? "none"
            : theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          width: isMobile ? "100%" : "70%",
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          {/* Chart Header */}
          <Box sx={{ mb: isMobile ? 2 : 3, textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                fontSize: isMobile ? "1rem" : "1.25rem",
                mb: 1,
              }}
            >
              Monthly Price Performance
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {isPositive ? (
                <TrendingUpIcon sx={{ color: "#22c55e", fontSize: isMobile ? 16 : 18 }} />
              ) : (
                <TrendingDownIcon sx={{ color: "#ef4444", fontSize: isMobile ? 16 : 18 }} />
              )}
              <Typography
                variant="body1"
                sx={{
                  color: lineColor,
                  fontWeight: 600,
                  fontSize: isMobile ? "0.875rem" : "1rem",
                }}
              >
                {isPositive ? "+" : ""}
                {formatCurrency(priceChange)} ({isPositive ? "+" : ""}
                {priceChangePercent.toFixed(2)}%)
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                }}
              >
                over report period
              </Typography>
            </Box>
          </Box>

          {/* Chart */}
          <Box sx={{ height: isMobile ? 220 : height }}>
            <ResponsiveLine
              data={chartData}
              margin={{
                top: isMobile ? 10 : 20,
                right: isMobile ? 10 : 20,
                bottom: isMobile ? 50 : 50,
                left: isMobile ? 55 : 60,
              }}
              xScale={{
                type: "point",
              }}
              yScale={{
                type: "linear",
                min: "auto",
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              yFormat=" >-.2f"
              curve="linear"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 0,
                tickPadding: isMobile ? 8 : 5,
                tickRotation: -45,
                format: formatAxisDate,
                tickValues: (() => {
                  const targetLabels = isMobile ? 5 : 10;
                  return Array.from({ length: targetLabels }, (_, i) =>
                    Math.floor((i * (sortedData.length - 1)) / (targetLabels - 1))
                  ).filter(
                    (value, index, array) =>
                      value < sortedData.length && array.indexOf(value) === index
                  );
                })(),
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: isMobile ? 8 : 5,
                tickRotation: 0,
                format: (value) => formatStockPriceCompact(value as number, currency),
                tickValues: isMobile ? 4 : 5,
              }}
              pointSize={0}
              pointColor={lineColor}
              pointBorderWidth={0}
              useMesh={false}
              colors={[lineColor]}
              lineWidth={2.5}
              enableArea={true}
              areaOpacity={0.4}
              areaBaselineValue={minPrice}
              enableGridX={false}
              enableGridY={true}
              gridYValues={isMobile ? 4 : 5}
              enableSlices={false}
              theme={{
                background: "transparent",
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                },
                axis: {
                  domain: {
                    line: {
                      stroke: "transparent",
                      strokeWidth: 0,
                    },
                  },
                  legend: {
                    text: {
                      fontSize: isMobile ? 10 : 12,
                      fill:
                        theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                    },
                  },
                  ticks: {
                    line: {
                      stroke: "transparent",
                      strokeWidth: 0,
                    },
                    text: {
                      fontSize: isMobile ? 10 : 11,
                      fill:
                        theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                      fontWeight: 600,
                    },
                  },
                },
                grid: {
                  line: {
                    stroke:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.08)",
                    strokeWidth: 1,
                  },
                },
              }}
              isInteractive={false}
              defs={[
                {
                  id: 'priceGradient',
                  type: 'linearGradient',
                  colors: [
                    { offset: 0, color: lineColor, opacity: 0.5 },
                    { offset: 50, color: lineColor, opacity: 0.25 },
                    { offset: 100, color: lineColor, opacity: 0 },
                  ],
                },
              ]}
              fill={[{ match: '*', id: 'priceGradient' }]}
            />
          </Box>

          {/* Chart Footer */}
          <Box sx={{ mt: isMobile ? 1.5 : 2, textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                fontSize: isMobile ? "0.7rem" : "0.75rem",
              }}
            >
              Price data for the month prior to report generation
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportPriceChart;
